import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import { CircularProgress } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import WaveSurfer from "wavesurfer.js";
import AudioRecorder from "../../../../assets/icons/AudioRecorder";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { getSegmentColorPalette } from "../../../../redux/selectors/segmentBuilderSelectors";
import { colors } from "../../../../utils";
import TextView from "../../../TextView";
import MediaFallback from "../MediaFallback";
import { useReportBrokenBlockMedia } from "../BrokenMedia/useReportBrokenBlockMedia";

const LoaderDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  border: 2px solid ${(props) => props.borderColor};
  border-radius: 8px;
`;

const Container = styled.div`
  padding: 12px;
  border-radius: 10px;
  background: ${(props) => props.backgroundColor};
  align-items: center;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 10px;
  align-items: center;
`;

const Image = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 10px;
  object-fit: cover;
`;

const WaveContainer = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  background: white;
  padding: 10px 0;
  height: 60px;
  width: 100%;
  border-radius: 10px;
  align-items: center;
  justify-content: center;
`;

const Icon = styled.div`
  color: ${(props) => props.color};
  font-size: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Waveform = styled.div`
  cursor: pointer;
`;

const CurrentTime = styled(TextView)`
  margin-left: 5px;
  color: ${colors.lightGrey};
`;

// Card height for each provider's embed iframe -- their own player chrome,
// not ours, so we size the frame to fit it rather than crop/scroll it.
const PROVIDER_IFRAME_HEIGHT = {
  spotify: 152,
  soundcloud: 166,
  youtube: 200,
  mixcloud: 120,
  deezer: 152,
  bandcamp: 120,
  apple_music: 175,
  apple_podcasts: 175,
  tidal: 96,
  audiomack: 252,
};

const PROVIDER_LABEL = {
  spotify: "Spotify",
  soundcloud: "SoundCloud",
  youtube: "YouTube",
  mixcloud: "Mixcloud",
  deezer: "Deezer",
  bandcamp: "Bandcamp",
  apple_music: "Apple Music",
  apple_podcasts: "Apple Podcasts",
  tidal: "Tidal",
  audiomack: "Audiomack",
};

const ProviderCard = styled.div`
  padding: 12px;
  border-radius: 10px;
  background: ${(props) => (props.dark ? "#161616" : props.backgroundColor)};
`;

const ProviderHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
`;

const ProviderThumb = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: cover;
`;

const ProviderMark = styled.span`
  font-size: 11px;
  opacity: 0.65;
`;

const IframeWrapper = styled.div`
  border-radius: 8px;
  overflow: hidden;
`;

const DynamicRenderer = ({ element }) => {
  const renderedElement = React.createElement(element.type, {
    ...element.props,
    key: element.key,
  });

  return <>{renderedElement}</>;
};

const AudioBlock = ({
  audio,
  textColor = colors.black,
  waveColor = colors.darkGrey,
  backgroundColor = colors.background,
  iconColor = colors.PrimaryPurple,
  showIcon = true,
}) => {
  const waveformRef = useRef(null);
  const waveSurferRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState("0:00");
  // Defensive: a broken/unreachable audio source shows a clean placeholder
  // instead of an empty, non-functional player. Only set on a real load error.
  const [hasError, setHasError] = useState(false);
  // A broken cover-art thumbnail falls back to the default audio icon rather
  // than a broken <img>; the player itself stays usable.
  const [thumbError, setThumbError] = useState(false);

  const selectedPalette = useSelector(getSegmentColorPalette);

  const { getPaletteColor } = usePaletteColor();

  // T22.688: an audio file that can't be fetched/decoded -> the broken-media pill.
  const reportBrokenAudio = useReportBrokenBlockMedia("media_block");
  useEffect(() => {
    if (hasError && audio?.src && !audio?.embed) reportBrokenAudio(audio.src);
  }, [hasError, audio, reportBrokenAudio]);

  // Fresh source = fresh attempt; clear any previous error.
  useEffect(() => {
    setHasError(false);
  }, [audio?.src]);

  useEffect(() => {
    // T22.688: once a load error swaps the player for the placeholder the
    // waveform container is gone; WaveSurfer.create(null) threw "Container not
    // found" and took the whole page to the crash screen. Skip until the
    // player is back (hasError cleared by a new source).
    if (audio.src && !audio.embed && waveformRef.current) {
      waveSurferRef.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: colors.lightGrey,
        progressColor: getPaletteColor(waveColor),
        responsive: true,
        normalize: true,
        barWidth: 2,
        width: "100%",
        height: 30,
      });

      // load() also rejects on a fetch error (the 'error' event below handles it).
      Promise.resolve(waveSurferRef.current.load(audio.src)).catch(() => {});
      // Defensive: surface a clean fallback if the audio can't be decoded/fetched.
      waveSurferRef.current.on("error", () => {
        setHasError(true);
      });
      waveSurferRef.current.on("audioprocess", () => {
        const current = waveSurferRef.current.getCurrentTime();
        setCurrentTime(formatTime(current));
      });

      waveSurferRef.current.on("seeking", () => {
        const current = waveSurferRef.current.getCurrentTime();
        setCurrentTime(formatTime(current));
      });
    }

    return () => {
      if (waveSurferRef.current) waveSurferRef.current.destroy();
    };
  }, [audio, waveColor, selectedPalette, hasError]);

  const handlePlayPause = () => {
    if (waveSurferRef.current) {
      if (isPlaying) {
        waveSurferRef.current.pause();
      } else {
        waveSurferRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  if (audio.loading)
    return (
      <LoaderDiv borderColor={getPaletteColor(backgroundColor)}>
        <CircularProgress style={{ color: getPaletteColor(iconColor) }} />
      </LoaderDiv>
    );

  if (audio.embed) {
    const { embedCode } = audio;
    return (
      <div style={{ borderRadius: 8, overflow: "hidden" }}>
        <DynamicRenderer element={embedCode[0]} />
      </div>
    );
  } else if (audio.link && audio.embedUrl) {
    // AUDIO_LINK_EMBED_ENABLED: a resolved provider embed (Spotify/Apple
    // Music/YouTube Music/SoundCloud/Bandcamp/Mixcloud/Deezer/Tidal/
    // Audiomack). The provider's OWN player renders inside our on-brand
    // card (thumbnail/title/author header + provider mark); the iframe
    // itself only ever gets the safe embedUrl the backend resolver
    // returned -- never user-pasted or provider-returned HTML.
    const isDark = Boolean(audio.theme?.dark);
    const headerColor = isDark ? "#ffffff" : getPaletteColor(textColor);
    return (
      <ProviderCard
        dark={isDark}
        backgroundColor={getPaletteColor(backgroundColor)}
      >
        <ProviderHeaderRow>
          {audio.image && !thumbError ? (
            <ProviderThumb
              src={audio.image}
              alt={audio.title || ""}
              onError={() => setThumbError(true)}
            />
          ) : (
            <AudioRecorder
              fill={getPaletteColor(iconColor)}
              width={44}
              height={44}
            />
          )}
          <div>
            <TextView
              color={headerColor}
              variant="subtitle"
              style={{ WebkitLineClamp: 1 }}
            >
              {audio.title || "Untitled"}
            </TextView>
            <TextView color={headerColor} variant="tag" style={{ opacity: 0.8 }}>
              {audio.author}
            </TextView>
          </div>
        </ProviderHeaderRow>
        <IframeWrapper>
          <iframe
            key={audio.embedUrl}
            src={audio.embedUrl}
            title={audio.title || PROVIDER_LABEL[audio.provider] || "Audio"}
            loading="lazy"
            sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
            allow="autoplay; encrypted-media"
            style={{
              border: 0,
              width: "100%",
              height: PROVIDER_IFRAME_HEIGHT[audio.provider] || 152,
              display: "block",
            }}
          />
        </IframeWrapper>
        <ProviderMark style={{ color: headerColor }}>
          {PROVIDER_LABEL[audio.provider] || audio.provider}
        </ProviderMark>
      </ProviderCard>
    );
  } else if (hasError) {
    return <MediaFallback variant="audio" compact />;
  } else
    return (
      <Container backgroundColor={getPaletteColor(backgroundColor)}>
        <TextView
          color={getPaletteColor(textColor)}
          variant="subtitle"
          style={{ WebkitLineClamp: 2, marginBottom: 10 }}
        >
          {audio.title}
        </TextView>
        <GridContainer
          style={{ gridTemplateColumns: showIcon ? "60px 1fr" : "1fr" }}
        >
          {showIcon &&
            (audio.image && !thumbError ? (
              <Image
                src={audio.image}
                alt={audio.title || ""}
                onError={() => setThumbError(true)}
              />
            ) : (
              <AudioRecorder
                fill={getPaletteColor(iconColor)}
                width={60}
                height={60}
              />
            ))}

          <WaveContainer>
            <Icon color={getPaletteColor(iconColor)} onClick={handlePlayPause}>
              {isPlaying ? <PauseCircleFilledIcon /> : <PlayCircleFilledIcon />}
            </Icon>
            <Waveform ref={waveformRef} />
            <CurrentTime variant="tag">{currentTime}</CurrentTime>
          </WaveContainer>
        </GridContainer>
      </Container>
    );
};

export default AudioBlock;
